import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Btn, Empty } from "../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, SelectWithCustom } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Drug", "Food", "Environmental", "Insect", "Biologic", "Other"];
const SEVERITIES = ["Minimal", "Mild", "Moderate", "Severe", "Life-Threatening"];
const STATUSES = ["Active", "Inactive", "Resolved"];
const VERIFICATIONS = ["Confirmed", "Unconfirmed", "Refuted"];
const COMMON_ALLERGENS = ["Penicillin", "Sulfa drugs", "Aspirin", "Ibuprofen (NSAIDs)", "Codeine", "Latex", "Peanuts", "Tree nuts", "Shellfish", "Eggs", "Milk/Dairy", "Soy", "Wheat/Gluten", "Bee stings", "Pollen", "Dust mites", "Pet dander", "Mold"];
const SEV_COLORS = { "Life-Threatening": "text-[#C62828]", Severe: "text-[#B84A00]", Moderate: "text-[#876600]" };
const EMPTY = { allergy: "", type: "Drug", severity: "Mild", reaction: "", reaction_category: "",
                 source: "Patient reported", onset_date: "", status: "Active",
                 verification: "Confirmed", last_occurrence: "", verified_by: "", notes: "" };

export function AllergiesPanel({ client, onUpdated }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${client.id}/allergies`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncClient = () => api.get(`/clients/${client.id}`).then((r) => onUpdated?.(r.data));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/allergies/${editId}`, form);
      else await api.post(`/clients/${client.id}/allergies`, form);
      toast.success(editId ? "Allergy updated" : "Allergy added");
      setPanel(false); load(); syncClient();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/allergies/${confirmDelete.id}`);
      toast.success("Allergy deleted");
      setConfirmDelete(null); load(); syncClient();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="allergies-panel">
      <div className="flex items-center justify-between mb-4">
        {!editable ? <ReadOnlyChip /> : <span />}
        {editable && <Btn data-testid="add-allergy-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(EMPTY); setEditId(null); setPanel(true); }}><Plus size={14} /> Add Allergy</Btn>}
      </div>
      {items.length === 0 ? <Empty text="No known allergies on file" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`allergy-card-${a.id}`} className={`rounded-2xl border p-4 ${a.status === "Resolved" || a.status === "Inactive" ? "bg-[#FAFAF7] border-[#EFEBE0] opacity-75" : "bg-[#F9F5EE] border-[#F1E8DA]"}`}>
              <div className="grid grid-cols-3 gap-3">
                <div><div className="font-heading font-bold text-[#3E2723]">{a.type || "-"}</div><div className="text-xs text-[#84675B] mt-0.5">Type</div></div>
                <div><div className={`font-heading font-bold ${SEV_COLORS[a.severity] || "text-[#3E2723]"}`}>{a.severity || "-"}</div><div className="text-xs text-[#84675B] mt-0.5">Severity</div></div>
                <div className="min-w-0"><div className="font-heading font-bold text-[#3E2723] break-words">{a.allergy}</div><div className="text-xs text-[#84675B] mt-0.5">Allergy</div></div>
              </div>
              <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#7B6157]">
                {a.status && <span>Status: <span className={a.status === "Active" ? "font-semibold text-[#2E7D32]" : "font-semibold"}>{a.status}</span></span>}
                {a.verification && <span>{a.verification}</span>}
                {a.onset_date && <span>Onset: {a.onset_date}</span>}
                {a.last_occurrence && <span>Last: {a.last_occurrence}</span>}
              </div>
              <div className="mt-2.5 flex items-end justify-between gap-2">
                <div className="text-sm font-semibold text-[#D32F2F] min-h-[20px]">
                  {a.reaction ? <span className="inline-flex items-center gap-1.5"><AlertTriangle size={13} /> Reaction: {a.reaction}</span> : <span className="text-[#84675B] font-normal">-</span>}
                </div>
                {editable && (
                  <div className="flex gap-1.5 shrink-0">
                    <button type="button" data-testid={`edit-allergy-${a.id}`} aria-label="Edit" onClick={() => { setForm({ ...EMPTY, ...a }); setEditId(a.id); setPanel(true); }}
                      className="h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                    <button type="button" data-testid={`delete-allergy-${a.id}`} aria-label="Delete" onClick={() => setConfirmDelete(a)}
                      className="h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Allergy" : "Add Allergy"}
        subtitle={`${client.first_name} ${client.last_name} - allergy & intolerance record`} testId="allergy-slide-panel">
        <form onSubmit={save} className="space-y-5">
          <div><FLabel>Allergy / Allergen *</FLabel>
            <SelectWithCustom testId="allergy-name-input" value={form.allergy} onChange={(v) => setForm((f) => ({ ...f, allergy: v }))}
              options={COMMON_ALLERGENS} customPlaceholder="Enter allergen name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>Category / Type</FLabel>
              <FSelect data-testid="allergy-type-input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </FSelect></div>
            <div><FLabel>Severity</FLabel>
              <FSelect data-testid="allergy-severity-input" value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>
                {SEVERITIES.map((s) => <option key={s}>{s}</option>)}
              </FSelect></div>
            <div><FLabel>Clinical Status</FLabel>
              <FSelect data-testid="allergy-status-input" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </FSelect></div>
            <div><FLabel>Verification</FLabel>
              <FSelect data-testid="allergy-verification-input" value={form.verification} onChange={(e) => setForm((f) => ({ ...f, verification: e.target.value }))}>
                {VERIFICATIONS.map((v) => <option key={v}>{v}</option>)}
              </FSelect></div>
            <div><FLabel>Onset Date</FLabel><DateField testId="allergy-onset-input" value={form.onset_date} onChange={(v) => setForm((f) => ({ ...f, onset_date: v }))} /></div>
            <div><FLabel>Last Occurrence</FLabel><DateField testId="allergy-last-occurrence-input" value={form.last_occurrence} onChange={(v) => setForm((f) => ({ ...f, last_occurrence: v }))} /></div>
          </div>
          <div><FLabel>Reaction / Manifestation</FLabel><FInput data-testid="allergy-reaction-input" placeholder="e.g. Rash, hives, anaphylaxis, angioedema" value={form.reaction} onChange={(e) => setForm((f) => ({ ...f, reaction: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Reaction Category</FLabel>
              <FSelect data-testid="allergy-reaction-category" value={form.reaction_category} onChange={(e) => setForm((f) => ({ ...f, reaction_category: e.target.value }))}>
                {["", "Cutaneous (rash, hives)", "GI (nausea, vomiting)", "Respiratory (wheeze, dyspnea)",
                   "Anaphylaxis", "Angioedema", "Neurologic", "Cardiovascular", "Other"].map((s) => <option key={s || "none"} value={s}>{s || "Not specified"}</option>)}
              </FSelect>
            </div>
            <div><FLabel>Source</FLabel>
              <FSelect data-testid="allergy-source" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
                {["Patient reported", "Family reported", "Chart record", "Provider observed", "Prior EHR"].map((s) => <option key={s}>{s}</option>)}
              </FSelect>
            </div>
            <div><FLabel>Verified By</FLabel><FInput data-testid="allergy-verified-by" value={form.verified_by} onChange={(e) => setForm((f) => ({ ...f, verified_by: e.target.value }))} placeholder="Provider name" /></div>
            <div><FLabel>Notes</FLabel><FInput data-testid="allergy-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="allergy-save-btn" type="submit" disabled={busy || !form.allergy.trim()}>{busy ? "Saving…" : editId ? "Save Changes" : "Add Allergy"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={doDelete} busy={busy}
        title="Delete allergy record"
        description={`Remove "${confirmDelete?.allergy}" (${confirmDelete?.severity}) from ${client.first_name}'s allergy list? This affects clinical safety alerts.`} />
    </div>
  );
}
